import React, { useState, useEffect } from "react";
import PageHeader from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import { formatDate, formatTime } from "@/lib/format";
import { 
  Settings as SettingsIcon, Pill, BarChart3, Plus, Trash2, ArrowLeft, 
  Clock, User, CalendarX, FileText, ShieldCheck, Save, CalendarDays,
  Building, CreditCard, Sparkles
} from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { 
  listAvailability, upsertAvailability, deleteAvailability, 
  listBlocks, createBlock, deleteBlock,
  getProfessionalProfile, updateProfessionalProfile, updateProfile
} from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";

const DAYS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

// Credit Toggle Switch component
function CreditToggle({ enabled, onChange, busy }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={busy}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand))] focus-visible:ring-offset-2 disabled:opacity-50 ${
        enabled ? "bg-[hsl(var(--brand))]" : "bg-[hsl(var(--border-divider))]"
      }`}
    >
      <span
        className={`pointer-events-none block h-5 w-5 rounded-full bg-white shadow-md ring-0 transition-transform ${
          enabled ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

export function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const view = searchParams.get("tab") || "menu";

  const setView = (v) => {
    if (v === "menu") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: v });
    }
  };

  const onBack = () => setView("menu");

  if (view === "profile") return <ProfileSettings onBack={onBack} />;
  if (view === "availability") return <AvailabilityManager onBack={onBack} />;
  if (view === "blocks") return <BlockManager onBack={onBack} />;
  if (view === "templates") return <TemplateSettings onBack={onBack} />;
  if (view === "privacy") return <PrivacySettings onBack={onBack} />;
  if (view === "billing") return <BillingSettings onBack={onBack} />;

  const menuItems = [
    { id: "profile", title: "Perfil profissional", desc: "Nome, CRP, especialidade e biografia.", icon: User },
    { id: "availability", title: "Horários de Atendimento", desc: "Defina sua grade de horários para agendamento.", icon: Clock },
    { id: "blocks", title: "Folgas e Bloqueios", desc: "Bloqueie datas específicas ou plantões recorrentes.", icon: CalendarX },
    { id: "clinic", title: "Minha Clínica", desc: "Gestão de equipe, membros e dados corporativos.", icon: Building, path: "/app/clinica" },
    { id: "billing", title: "Assinatura e Planos", desc: "Gerencie seu plano PsicoApp e faturamento.", icon: CreditCard },
    { id: "templates", title: "Modelos de evolução", desc: "Crie templates SOAP personalizados.", icon: FileText },
    { id: "privacy", title: "Privacidade & LGPD", desc: "Logs de acesso, exportação e exclusão de dados.", icon: ShieldCheck },
  ];

  return (
    <div className="ds-fade-up">
      <PageHeader eyebrow="Conta" title="Configurações" description="Personalize sua clínica, integrações e preferências." />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {menuItems.map((item) => (
          <div 
            key={item.id} 
            onClick={() => item.path ? navigate(item.path) : setView(item.id)} 
            className="ds-card ds-card-hover p-6 cursor-pointer flex flex-col group"
          >
            <div className="w-11 h-11 rounded-xl bg-[hsl(var(--brand-light))] text-[hsl(var(--brand))] flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
              <item.icon className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold tracking-tight mb-1" style={{fontFamily:"'Outfit',sans-serif"}}>{item.title}</h3>
            <p className="text-sm text-[hsl(var(--text-secondary))] leading-snug">{item.desc}</p>
            <div className="mt-auto pt-4 flex items-center text-[hsl(var(--brand))] text-xs font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
              Configurar →
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubPageLayout({ title, description, onBack, children, actions }) {
  return (
    <div className="ds-fade-up">
      {/* Mobile Sticky Header */}
      <div className="md:hidden sticky top-[64px] z-10 bg-[hsl(var(--bg-base))]/95 backdrop-blur -mx-4 px-4 py-3 border-b border-[hsl(var(--border-divider))] mb-6 flex items-center gap-3">
        <button 
          onClick={onBack} 
          className="w-10 h-10 flex items-center justify-center text-[hsl(var(--text-primary))] active:bg-[hsl(var(--bg-hover))] rounded-full transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="min-w-0">
          <h2 className="text-lg font-bold truncate leading-tight">{title}</h2>
          <p className="text-[10px] text-[hsl(var(--text-tertiary))] uppercase tracking-wider font-semibold">{description.slice(0, 40)}...</p>
        </div>
        {actions && <div className="ml-auto">{actions}</div>}
      </div>

      {/* Desktop Header */}
      <div className="hidden md:flex items-center gap-4 mb-8">
        <button 
          onClick={onBack} 
          className="w-11 h-11 rounded-full border border-[hsl(var(--border-default))] bg-white flex items-center justify-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--brand))] hover:border-[hsl(var(--brand))] transition-all shadow-sm group"
          title="Voltar para o menu"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
        </button>
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>{title}</h2>
          <p className="text-sm text-[hsl(var(--text-tertiary))]">{description}</p>
        </div>
        {actions && <div className="ml-auto">{actions}</div>}
      </div>

      <div className="pb-10">
        {children}
      </div>
    </div>
  );
}

function ProfileSettings({ onBack }) {
  const { profile, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    crp: "",
    specialties: "",
    bio: "",
    professional_type: "",
    session_price: 150,
    discount_monthly: 5,
    discount_trimestral: 10,
    discount_semestral: 15,
    use_credits: true,
  });

  useEffect(() => {
    if (profile?.id) {
      getProfessionalProfile(profile.id).then(data => {
        if (data) {
          setFormData({
            full_name: profile.full_name || "",
            crp: data.crp || "",
            specialties: data.specialties?.join(", ") || "",
            bio: data.bio || "",
            professional_type: data.professional_type || "",
            session_price: data.session_price || 150,
            discount_monthly: data.discount_monthly ?? 5,
            discount_trimestral: data.discount_trimestral ?? 10,
            discount_semestral: data.discount_semestral ?? 15,
            use_credits: data.use_credits ?? true,
          });
        }
      }).finally(() => setLoading(false));
    }
  }, [profile]);

  const handleSave = async () => {
    setBusy(true);
    try {
      // 1. Update Profile (Base)
      await updateProfile(profile.id, { full_name: formData.full_name });
      
      // 2. Update Professional Profile
      await updateProfessionalProfile(profile.id, {
        crp: formData.crp,
        bio: formData.bio,
        professional_type: formData.professional_type || null,
        session_price: Number(formData.session_price),
        discount_monthly: Number(formData.discount_monthly),
        discount_trimestral: Number(formData.discount_trimestral),
        discount_semestral: Number(formData.discount_semestral),
        use_credits: formData.use_credits,
        specialties: formData.specialties.split(",").map(s => s.trim()).filter(Boolean)
      });

      await refreshProfile();
      alert("Perfil atualizado com sucesso!");
    } catch (e) {
      alert("Erro ao salvar: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-20 text-center animate-pulse">Carregando perfil...</div>;

  return (
    <SubPageLayout 
      title="Perfil Profissional" 
      description="Gerencie suas informações públicas e credenciais clínicas."
      onBack={onBack}
      actions={
        <button disabled={busy} onClick={handleSave} className="ds-btn-primary gap-2 px-4 py-2">
          <Save className="w-4 h-4" /> {busy ? "Salvando..." : "Salvar Alterações"}
        </button>
      }
    >
      <div className="ds-card p-4 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div>
              <label className="ds-label">Nome Completo</label>
              <input type="text" className="ds-input" value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="ds-label">Registro (CRP/CRM)</label>
                <input type="text" className="ds-input" placeholder="Ex: 06/123456" value={formData.crp} onChange={e => setFormData({...formData, crp: e.target.value})} />
              </div>
              <div>
                <label className="ds-label text-[hsl(var(--brand))] font-bold">Valor da Sessão (R$)</label>
                <input type="number" className="ds-input border-[hsl(var(--brand))] focus:ring-[hsl(var(--brand))]" placeholder="150" value={formData.session_price} onChange={e => setFormData({...formData, session_price: e.target.value})} />
              </div>
            </div>

            {/* Tipo de Profissional */}
            <div>
              <label className="ds-label">Tipo de Profissional</label>
              <select
                className="ds-input"
                value={formData.professional_type}
                onChange={e => setFormData({...formData, professional_type: e.target.value})}
              >
                <option value="">Selecione...</option>
                <option value="psicologo">Psicólogo(a)</option>
                <option value="medico">Médico(a)</option>
                <option value="fisioterapeuta">Fisioterapeuta</option>
                <option value="dentista">Dentista</option>
                <option value="outro">Outro</option>
              </select>
              <p className="text-[10px] text-[hsl(var(--text-tertiary))] mt-1 italic">
                Define o modelo de anamnese exibido nos prontuários dos seus pacientes.
              </p>
            </div>
            
            <div className="p-4 bg-[hsl(var(--brand-light))]/5 rounded-xl border border-[hsl(var(--brand))]/10 space-y-4">
              <div className="ds-overline text-[hsl(var(--brand))]">Descontos por Plano (%)</div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="ds-label !text-[10px]">Mensal (4s)</label>
                  <input type="number" className="ds-input ds-input-sm" value={formData.discount_monthly} onChange={e => setFormData({...formData, discount_monthly: e.target.value})} />
                </div>
                <div>
                  <label className="ds-label !text-[10px]">Trimestral (12s)</label>
                  <input type="number" className="ds-input ds-input-sm" value={formData.discount_trimestral} onChange={e => setFormData({...formData, discount_trimestral: e.target.value})} />
                </div>
                <div>
                  <label className="ds-label !text-[10px]">Semestral (24s)</label>
                  <input type="number" className="ds-input ds-input-sm" value={formData.discount_semestral} onChange={e => setFormData({...formData, discount_semestral: e.target.value})} />
                </div>
              </div>
              <p className="text-[10px] text-[hsl(var(--text-tertiary))] italic">O desconto é aplicado sobre o valor total das sessões do pacote.</p>
            </div>

            <div>
              <label className="ds-label">Especialidades</label>
              <input type="text" className="ds-input" placeholder="Ex: TCC, Psicanálise (separe por vírgula)" value={formData.specialties} onChange={e => setFormData({...formData, specialties: e.target.value})} />
            </div>
            
            <div className="col-span-1 md:col-span-2">
              {/* Credit System Toggle */}
              <div className={`p-4 rounded-xl border-2 transition-colors ${
                formData.use_credits
                  ? "bg-[hsl(var(--brand-light))]/5 border-[hsl(var(--brand))]/20"
                  : "bg-[hsl(var(--bg-muted))] border-[hsl(var(--border-default))]"
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
                      formData.use_credits ? "bg-[hsl(var(--brand))] text-white" : "bg-[hsl(var(--border-divider))] text-[hsl(var(--text-tertiary))]"
                    }`}>
                      <CreditCard className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-[hsl(var(--text-primary))]">Controle de Créditos por Sessão</div>
                      <div className="text-[11px] text-[hsl(var(--text-secondary))] mt-0.5">
                        {formData.use_credits
                          ? "Ativado — pacientes precisam de saldo para agendar."
                          : "Desativado — agendamentos livres, sem controle de saldo."}
                      </div>
                    </div>
                  </div>
                  <CreditToggle
                    enabled={formData.use_credits}
                    onChange={(v) => setFormData({...formData, use_credits: v})}
                    busy={busy}
                  />
                </div>
                {!formData.use_credits && (
                  <div className="mt-3 p-3 bg-[hsl(var(--bg-base))] rounded-lg border border-dashed border-[hsl(var(--border-default))]">
                    <p className="text-[10px] text-[hsl(var(--text-tertiary))] leading-relaxed">
                      <strong>Modo livre:</strong> Os agendamentos não são bloqueados por saldo. O histórico de créditos já registrado é mantido — você pode reativar a qualquer momento.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="ds-label">Biografia / Sobre</label>
              <textarea className="ds-input min-h-[150px]" placeholder="Conte um pouco sobre sua formação e abordagem..." value={formData.bio} onChange={e => setFormData({...formData, bio: e.target.value})}></textarea>
            </div>
          </div>
        </div>
      </div>
    </SubPageLayout>
  );
}

function TemplateSettings({ onBack }) {
  return (
    <SubPageLayout 
      title="Modelos de Evolução" 
      description="Crie e gerencie templates para agilizar seus registros SOAP."
      onBack={onBack}
    >
      <EmptyState 
        icon={FileText} 
        title="Templates Personalizados" 
        description="Em breve você poderá criar modelos de texto para cada parte do SOAP, agilizando o preenchimento das sessões." 
      />
    </SubPageLayout>
  );
}

function PrivacySettings({ onBack }) {
  return (
    <SubPageLayout 
      title="Privacidade & LGPD" 
      description="Segurança dos dados, logs de auditoria e conformidade."
      onBack={onBack}
    >
      <div className="space-y-4">
        <div className="ds-card p-6 flex items-center justify-between">
          <div>
            <h4 className="font-bold">Exportar Meus Dados</h4>
            <p className="text-sm text-[hsl(var(--text-secondary))]">Gere um arquivo com todos os seus registros e dados de pacientes.</p>
          </div>
          <button className="ds-btn-ghost text-sm">Solicitar Exportação</button>
        </div>
        <div className="ds-card p-6 border-red-100 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-red-600">Excluir Conta</h4>
            <p className="text-sm text-[hsl(var(--text-secondary))]">Ação irreversível. Todos os dados serão permanentemente removidos.</p>
          </div>
          <button className="ds-btn-ghost text-red-600 text-sm hover:bg-red-50">Excluir Tudo</button>
        </div>
      </div>
    </SubPageLayout>
  );
}

function BillingSettings({ onBack }) {
  const { profile, loading } = useAuth();

  if (loading) {
    return (
      <SubPageLayout 
        title="Assinatura e Planos" 
        description="Gerencie seu plano de uso da plataforma e métodos de pagamento."
        onBack={onBack}
      >
        <div className="py-20 text-center animate-pulse text-sm text-[hsl(var(--text-tertiary))]">
          Carregando informações financeiras...
        </div>
      </SubPageLayout>
    );
  }

  const plans = {
    solo: {
      name: "Solo",
      price: "R$ 49,90",
      desc: "Acesso completo a todas as ferramentas clínicas para profissionais independentes.",
      value: 49.90
    },
    essential: {
      name: "Clínica Essential",
      price: "R$ 89,90",
      desc: "Acesso ilimitado para até 3 profissionais e gestão centralizada da clínica.",
      value: 89.90
    },
    pro: {
      name: "Clínica Pro",
      price: "R$ 149,90",
      desc: "Acesso ilimitado a todas as ferramentas, multi-clínicas e repasses automáticos.",
      value: 149.90
    }
  };

  const currentPlan = plans[profile?.system_plan_id] || plans.solo;

  const statuses = {
    trial: { label: "Período de Testes", color: "bg-amber-500 text-white" },
    active: { label: "Ativo", color: "bg-emerald-600 text-white" },
    past_due: { label: "Atrasado", color: "bg-rose-600 text-white" },
    pending: { label: "Pendente", color: "bg-amber-500 text-white" }
  };

  // Se o status da assinatura for nulo, fazemos um fallback elegante com base em is_approved
  const statusInfo = statuses[profile?.subscription_status] || {
    label: profile?.is_approved ? "Ativo" : "Pendente",
    color: profile?.is_approved ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"
  };

  const expirationDate = profile?.subscription_expires_at 
    ? formatDate(profile.subscription_expires_at) 
    : "Não definida";

  // Obter o ano e mês atuais para formatar as faturas mock de forma realista com base no vencimento
  const baseYear = profile?.subscription_expires_at ? new Date(profile.subscription_expires_at).getFullYear() : 2026;

  return (
    <SubPageLayout 
      title="Assinatura e Planos" 
      description="Gerencie seu plano de uso da plataforma e métodos de pagamento."
      onBack={onBack}
    >
      <div className="space-y-6">
        <div className="ds-card p-6 border-brand bg-brand/5 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4">
            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-brand text-white flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-xl font-bold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Plano {currentPlan.name}</h4>
              <p className="text-sm text-[hsl(var(--text-secondary))]">{currentPlan.desc}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-2xl font-black text-brand" style={{fontFamily:"'Outfit',sans-serif"}}>{currentPlan.price}</span>
                <span className="text-xs text-[hsl(var(--text-tertiary))]">/ mês</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="ds-card p-6">
            <h5 className="font-bold mb-1">Próximo Vencimento</h5>
            <p className="text-sm text-[hsl(var(--text-secondary))] mb-4">Sua assinatura renova ou expira em:</p>
            <div className="text-lg font-bold text-[hsl(var(--text-primary))]">{expirationDate}</div>
          </div>
          <div className="ds-card p-6">
            <h5 className="font-bold mb-1">Método de Pagamento</h5>
            <p className="text-sm text-[hsl(var(--text-secondary))] mb-4">Cartão de crédito padrão:</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-6 bg-slate-100 rounded border border-slate-200 flex items-center justify-center text-[8px] font-black italic text-slate-400">VISA</div>
              <div className="text-sm font-medium">•••• 4242</div>
            </div>
          </div>
        </div>

        <div className="ds-card p-6">
          <h5 className="font-bold mb-4">Histórico de Faturas</h5>
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="flex items-center justify-between py-3 border-b border-[hsl(var(--border-divider))] last:border-0">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-[hsl(var(--text-tertiary))]" />
                  <div>
                    <div className="text-sm font-medium">Fatura #{baseYear}00{i}</div>
                    <div className="text-[10px] text-[hsl(var(--text-tertiary))] uppercase">Pago em {15 - i} de Mai, {baseYear}</div>
                  </div>
                </div>
                <div className="text-sm font-bold">{currentPlan.price}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SubPageLayout>
  );
}

function BlockManager({ onBack }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newBlock, setNewBlock] = useState({ date: "", start_time: "08:00", end_time: "18:00", reason: "Plantão", repeat_every: 0, repeat_times: 1 });

  const load = async () => {
    setLoading(true);
    try { setBlocks(await listBlocks()); } 
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    try {
      const baseDate = new Date(newBlock.date + "T" + newBlock.start_time);
      const baseEnd = new Date(newBlock.date + "T" + newBlock.end_time);
      
      const interval = parseInt(newBlock.repeat_every);
      const times = parseInt(newBlock.repeat_times) || 1;

      for (let i = 0; i < times; i++) {
        const startAt = new Date(baseDate.getTime() + (i * interval * 24 * 60 * 60 * 1000)).toISOString();
        const endAt = new Date(baseEnd.getTime() + (i * interval * 24 * 60 * 60 * 1000)).toISOString();
        
        await createBlock({ start_at: startAt, end_at: endAt, reason: newBlock.reason });
      }
      
      setAdding(false);
      load();
    } catch (e) { alert("Erro ao salvar: " + e.message); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Remover este bloqueio?")) return;
    try {
      await deleteBlock(id);
      load();
    } catch (e) { alert("Erro ao excluir: " + e.message); }
  };

  return (
    <SubPageLayout 
      title="Folgas e Bloqueios" 
      description="Trave datas ou períodos para impedir agendamentos de pacientes."
      onBack={onBack}
      actions={
        <button onClick={() => setAdding(true)} className="ds-btn-primary py-2 px-4 gap-2">
          <Plus className="w-4 h-4" /> Novo Bloqueio
        </button>
      }
    >
      <div className="ds-card p-6 mb-6">
        <h3 className="text-sm font-bold uppercase tracking-widest text-[hsl(var(--text-tertiary))] mb-6">Bloqueios Ativos</h3>

        {loading ? <div className="py-10 animate-pulse text-center">Carregando bloqueios...</div> : blocks.length === 0 ? (
          <EmptyState icon={Clock} title="Tudo livre" description="Você não tem bloqueios agendados. Sua agenda segue a disponibilidade geral." />
        ) : (
          <div className="space-y-3">
            {blocks.map(b => (
              <div key={b.id} className="flex items-center justify-between p-4 bg-[hsl(var(--bg-base))] rounded-xl border border-[hsl(var(--border-default))] group hover:border-[hsl(var(--brand))] transition-colors">
                <div className="flex items-center gap-6">
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))] tracking-tighter">Data</span>
                    <div className="font-bold text-sm">{formatDate(b.start_at)}</div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))] tracking-tighter">Horário e Motivo</span>
                    <div className="text-sm text-[hsl(var(--text-secondary))] font-medium">
                      {b.reason} ({formatTime(b.start_at)} às {formatTime(b.end_at)})
                    </div>
                  </div>
                </div>
                <button onClick={() => handleDelete(b.id)} className="p-2 text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--danger-text))] hover:bg-[hsl(var(--danger-bg))] rounded-lg transition-all">
                  <Trash2 className="w-4.5 h-4.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {adding && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="ds-card w-full max-w-md p-8 ds-fade-up overflow-y-auto max-h-[90vh] shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
                <CalendarX className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-bold">Novo Bloqueio</h3>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="ds-label">Data de Início</label>
                <input type="date" className="ds-input" value={newBlock.date} onChange={e => setNewBlock({...newBlock, date: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="ds-label">Hora Início</label>
                  <input type="time" className="ds-input" value={newBlock.start_time} onChange={e => setNewBlock({...newBlock, start_time: e.target.value})} />
                </div>
                <div>
                  <label className="ds-label">Hora Fim</label>
                  <input type="time" className="ds-input" value={newBlock.end_time} onChange={e => setNewBlock({...newBlock, end_time: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="ds-label">Motivo (ex: Plantão, Férias)</label>
                <input type="text" className="ds-input" value={newBlock.reason} onChange={e => setNewBlock({...newBlock, reason: e.target.value})} />
              </div>

              <div className="p-4 bg-[hsl(var(--bg-base))] rounded-xl border border-[hsl(var(--border-default))] space-y-4">
                <div className="flex items-center gap-2 text-[hsl(var(--brand))]">
                  <CalendarDays className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase tracking-wider">Replicação automática</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-[10px] uppercase text-[hsl(var(--text-tertiary))] font-bold mb-1 block">A cada (dias)</span>
                    <input type="number" className="ds-input h-10" placeholder="0 = não repetir" value={newBlock.repeat_every} onChange={e => setNewBlock({...newBlock, repeat_every: e.target.value})} />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase text-[hsl(var(--text-tertiary))] font-bold mb-1 block">Quantas vezes</span>
                    <input type="number" className="ds-input h-10" value={newBlock.repeat_times} onChange={e => setNewBlock({...newBlock, repeat_times: e.target.value})} />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setAdding(false)} className="ds-btn-ghost flex-1">Cancelar</button>
              <button onClick={handleAdd} className="ds-btn-primary flex-1">Criar Bloqueio</button>
            </div>
          </div>
        </div>
      )}
    </SubPageLayout>
  );
}

function AvailabilityManager({ onBack }) {
  const { profile } = useAuth();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newSlot, setNewSlot] = useState({ day_of_week: 1, start_time: "08:00", end_time: "18:00" });

  const load = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try { setSlots(await listAvailability(profile.id)); } 
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [profile?.id]);

  const handleAdd = async () => {
    try {
      await upsertAvailability(newSlot);
      setAdding(false);
      load();
    } catch (e) { alert("Erro ao salvar: " + e.message); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Remover este horário?")) return;
    try {
      await deleteAvailability(id);
      load();
    } catch (e) { alert("Erro ao excluir: " + e.message); }
  };

  return (
    <SubPageLayout 
      title="Horários de Atendimento" 
      description="Sua grade de disponibilidade recorrente para agendamentos online."
      onBack={onBack}
      actions={
        <button onClick={() => setAdding(true)} className="ds-btn-primary py-2 px-4 gap-2">
          <Plus className="w-4 h-4" /> Novo Turno
        </button>
      }
    >
      <div className="ds-card p-6 mb-6">
        <h3 className="text-sm font-bold uppercase tracking-widest text-[hsl(var(--text-tertiary))] mb-6">Grade Semanal</h3>

        {loading ? <div className="py-10 animate-pulse text-center">Carregando horários...</div> : slots.length === 0 ? (
          <EmptyState icon={Clock} title="Nenhum horário definido" description="Adicione seus turnos de atendimento para que os pacientes possam agendar." />
        ) : (
          <div className="space-y-3">
            {slots.map(s => (
              <div key={s.id} className="flex items-center justify-between p-4 bg-[hsl(var(--bg-base))] rounded-xl border border-[hsl(var(--border-default))] group hover:border-[hsl(var(--brand))] transition-colors">
                <div className="flex items-center gap-6">
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))] tracking-tighter">Dia</span>
                    <div className="font-bold text-sm text-[hsl(var(--brand))]">{DAYS[s.day_of_week]}</div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))] tracking-tighter">Turno</span>
                    <div className="text-sm text-[hsl(var(--text-secondary))] font-medium">
                      {s.start_time.slice(0,5)} às {s.end_time.slice(0,5)}
                    </div>
                  </div>
                </div>
                <button onClick={() => handleDelete(s.id)} className="p-2 text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--danger-text))] hover:bg-[hsl(var(--danger-bg))] rounded-lg transition-all">
                  <Trash2 className="w-4.5 h-4.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {adding && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="ds-card w-full max-w-md p-8 ds-fade-up shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[hsl(var(--brand-light))] text-[hsl(var(--brand))] flex items-center justify-center">
                <Clock className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-bold">Adicionar Turno</h3>
            </div>

            <div className="space-y-5">
              <div>
                <label className="ds-label">Dia da semana</label>
                <select className="ds-input h-12" value={newSlot.day_of_week} onChange={e => setNewSlot({...newSlot, day_of_week: parseInt(e.target.value)})}>
                  {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="ds-label">Início</label>
                  <input type="time" className="ds-input h-12" value={newSlot.start_time} onChange={e => setNewSlot({...newSlot, start_time: e.target.value})} />
                </div>
                <div>
                  <label className="ds-label">Fim</label>
                  <input type="time" className="ds-input h-12" value={newSlot.end_time} onChange={e => setNewSlot({...newSlot, end_time: e.target.value})} />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setAdding(false)} className="ds-btn-ghost flex-1">Cancelar</button>
              <button onClick={handleAdd} className="ds-btn-primary flex-1">Salvar Turno</button>
            </div>
          </div>
        </div>
      )}
    </SubPageLayout>
  );
}

export function Prescriptions() {
  return (
    <div className="ds-fade-up">
      <PageHeader eyebrow="Cuidado contínuo" title="Prescrições" description="Modelos clínicos, orientações e prescrições não-medicamentosas." />
      <EmptyState icon={Pill} title="Em breve"
                  description="A área de prescrições com modelos reutilizáveis está em desenvolvimento. Por ora, registre orientações no plano (P) das evoluções." />
    </div>
  );
}

export function Reports() {
  return (
    <div className="ds-fade-up">
      <PageHeader eyebrow="Indicadores" title="Relatórios" description="Métricas clínicas e de gestão da sua prática." />
      <EmptyState icon={BarChart3} title="Em breve"
                  description="Em breve: evolução de humor por paciente, frequência de sessões, taxas de aderência e relatórios exportáveis." />
    </div>
  );
}
